import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Story } from '../../context/AppContext';
import { ArrowLeftIcon, MoonIcon, SearchIcon } from '../icons';
import StoryScene from './StoryScene';
import StorySearch from './StorySearch';
import { groupStoryWorlds, readChapterSelections, writeChapterSelections } from './storyWorldModel';
import './StoryWorlds.css';

interface Props {
  stories: Story[];
  storageKey: string;
  title: string;
  subtitle: string;
  library?: boolean;
  /** Route back to the overview the tile came from. */
  back?: { to: string; label: string };
  action: (story: Story) => React.ReactNode;
  detail?: (story: Story) => React.ReactNode;
  status?: (story: Story) => React.ReactNode;
  duration?: (story: Story) => string | null;
}

const StoryWorlds: React.FC<Props> = ({ stories, storageKey, title, subtitle, library = false, back, ...presentation }) => {
  const worlds = useMemo(() => groupStoryWorlds(stories), [stories]);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [navigation, setNavigation] = useState(0);
  const [motion, setMotion] = useState(true);
  const [selected, setSelected] = useState(() => readChapterSelections(storageKey));
  const [params, setParams] = useSearchParams();
  const requested = params.get('story');
  const lastRequest = useRef<string | null>(null);
  useEffect(() => {
    if (!requested || requested === lastRequest.current) return;
    const world = worlds.find((item) => item.chapters.some((chapter) => chapter.story_id === requested));
    if (!world) return;
    lastRequest.current = requested;
    setSelected((current) => {
      const next = { ...current, [world.id]: requested };
      writeChapterSelections(storageKey, next);
      return next;
    });
    const timer = window.setTimeout(() => {
      const scene = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[data-world-id]') || []).find((node) => node.dataset.worldId === world.id);
      scene?.scrollIntoView?.({ block: 'start', behavior: 'auto' });
      scene?.querySelector<HTMLElement>('h2')?.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [requested, worlds, storageKey, navigation]);
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    let lastTurn = -Infinity;
    let accumulated = 0;
    let previousWheel = 0;
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
      const copy = (event.target as HTMLElement).closest('.story-scene__copy');
      // Enlarged text remains independently scrollable when it needs more room.
      if (copy && copy.scrollHeight > copy.clientHeight + 1) return;
      event.preventDefault();
      const now = performance.now();
      if (now - previousWheel > 180) accumulated = 0;
      previousWheel = now;
      if (now - lastTurn < 650) return;
      accumulated += event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? list.clientHeight : 1);
      if (Math.abs(accumulated) < 30) return;
      const index = Math.round(list.scrollTop / list.clientHeight);
      const next = Math.max(0, Math.min(worlds.length - 1, index + Math.sign(accumulated)));
      lastTurn = now;
      accumulated = 0;
      list.scrollTo({ top: next * list.clientHeight, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };
    list.addEventListener('wheel', onWheel, { passive: false });
    return () => list.removeEventListener('wheel', onWheel);
  }, [worlds.length]);
  const select = (worldId: string, story: Story) => {
    setSelected((current) => {
      const next = { ...current, [worldId]: story.story_id };
      writeChapterSelections(storageKey, next);
      return next;
    });
  };
  return <div className="story-worlds" ref={rootRef}>
    <header className="story-worlds__toolbar">{back && <Link className="world-icon-button story-worlds__back" to={back.to} aria-label={back.label}><ArrowLeftIcon size={18} /></Link>}<div className="story-worlds__title"><h1>{title}</h1><p>{subtitle}</p></div><div className="story-worlds__tools">
      <button type="button" className="world-search-button" aria-label={library ? 'Search your stories' : 'Search stories'} onClick={(event) => { event.currentTarget.focus({ preventScroll: true }); setSearchOpen(true); }}><SearchIcon size={20} /><span>{library ? 'Search your stories' : 'Search stories'}</span></button>
      <button type="button" className="world-icon-button" aria-pressed={motion} aria-label={motion ? 'Turn off ambient motion' : 'Turn on ambient motion'} onClick={() => setMotion((value) => !value)}><MoonIcon size={18} /></button>
    </div></header>
    <div ref={listRef} className="story-worlds__list" tabIndex={0} aria-label="Story worlds" data-testid={library ? 'my-stories-list' : 'discover-worlds'}>
      {worlds.map((world, index) => <StoryScene key={world.id} world={world} selectedId={selected[world.id]} onSelect={(story) => select(world.id, story)} first={index === 0} motion={motion} {...presentation} />)}
    </div>
    {searchOpen && <StorySearch library={library} stories={stories} onClose={() => setSearchOpen(false)} onSelect={(story) => {
      setSearchOpen(false);
      lastRequest.current = null;
      setNavigation((value) => value + 1);
      const next = new URLSearchParams(params);
      next.set('story', story.story_id);
      setParams(next, { replace: true });
    }} />}
  </div>;
};
export default StoryWorlds;
